import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '../../../core/services/api';
import { api } from '../../../models/api-url.model';
import type {
  AssignToGroupResponse,
  ChangePasswordRequest,
  DeactivateOktaUserRequest,
  FederationCheckResponse,
  MakeFederatedResponse,
  OktaUserExistsResponse,
  OktaUserFactorsResponse,
  OktaUserMfa,
  OktaUserRequest,
  OktaUserResponse,
  OktaUsersListResponse,
  OktaUsersPage,
} from './okta-user.types';

/** Max page size the listOktaUsers controller accepts without includeMfa. */
export const OKTA_USERS_PAGE_LIMIT = 200;

@Injectable({
  providedIn: 'root',
})
export class OktaUserService {
  private readonly apiService = inject(ApiService);

  /**
   * Fetches a single page of users. MFA is not requested here: with
   * includeMfa=true every user on the page costs an Okta factors call, so MFA
   * is loaded per user via getUserFactors instead.
   */
  getUsersPage(after?: string | null): Observable<OktaUsersPage> {
    const params = new URLSearchParams({
      limit: String(OKTA_USERS_PAGE_LIMIT),
      includeMfa: 'false',
    });
    if (after) params.set('after', after);
    return this.apiService
      .get<OktaUsersListResponse>(`${api.oktaUsersURL}?${params}`)
      .pipe(map((res) => res.data));
  }

  getUserFactors(login: string): Observable<OktaUserMfa> {
    return this.apiService
      .get<OktaUserFactorsResponse>(
        `${api.oktaUsersURL}/${encodeURIComponent(login)}/factors`,
      )
      .pipe(map((res) => res.data));
  }

  checkFederation(username: string): Observable<FederationCheckResponse> {
    return this.apiService.get(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/is-federated`,
    );
  }

  checkExists(username: string): Observable<OktaUserExistsResponse> {
    return this.apiService.get(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/exists`,
    );
  }

  assignToGroup(username: string): Observable<AssignToGroupResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/assign-group`,
    );
  }

  makeFederated(username: string): Observable<MakeFederatedResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/make-federated`,
    );
  }

  createUser(payload: OktaUserRequest): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.oktaUsersURL}`, payload);
  }

  updateUser(payload: OktaUserRequest): Observable<OktaUserResponse> {
    return this.apiService.put(`${api.oktaUsersURL}/${payload.login}`, payload);
  }

  deactivateUser(
    payload: DeactivateOktaUserRequest,
  ): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${payload.login}/deactivate`,
      payload,
    );
  }

  resendActivationEmail(login: string): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/reactivate`,
    );
  }

  resendCustomActivationEmail(
    login: string,
    fromURI?: string,
  ): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/reactivate/custom-activation-email`,
      fromURI ? { fromURI } : {},
    );
  }

  onboardUser(payload: OktaUserRequest): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.onboardOktaUsersURL}`, payload);
  }

  onboardUserWithCustomActivationEmail(
    payload: OktaUserRequest,
  ): Observable<OktaUserResponse> {
    return this.apiService.post(
      `${api.onboardOktaUsersCustomActivationEmailURL}`,
      payload,
    );
  }

  changePassword(payload: ChangePasswordRequest): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.changeOktaPasswordURL}`, payload);
  }

  forgotPassword(login: string): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.forgotOktaPasswordURL}`, { login });
  }

  unlockUser(login: string): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/unlock`,
    );
  }

  resetMfaFactor(
    login: string,
    factorId: string,
  ): Observable<OktaUserResponse> {
    return this.apiService.delete(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/factors/${encodeURIComponent(factorId)}`,
    );
  }

  deleteUser(username: string): Observable<OktaUserResponse> {
    return this.apiService.delete(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}`,
    );
  }

  startOktaMigration(email: string): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.oktaMigrationURL}`, { email });
  }
}
